<template>
  <!-- Hero Section - Clean & Simple -->
  <section class="relative h-[80vh] min-h-[550px] max-h-[800px] bg-slate-900 overflow-hidden">
    <!-- Background Carousel -->
    <div class="absolute inset-0">
      <div
        v-for="(slide, index) in slides"
        :key="slide.id"
        class="absolute inset-0 transition-opacity duration-1000"
        :class="currentSlide === index ? 'opacity-100 z-10' : 'opacity-0 pointer-events-none'"
        role="group"
        :aria-roledescription="slide.type === 'video' ? 'video slide' : 'image slide'"
        :aria-label="`${slide.name} — slide ${index + 1} of ${slides.length}`"
      >
        <video
          v-if="slide.type === 'video' && shouldLoadSlide(index)"
          :src="slide.url"
          class="w-full h-full hero-media transition-transform duration-1000"
          :autoplay="currentSlide === index"
          muted
          :loop="currentSlide === index"
          playsinline
          :preload="currentSlide === index ? 'metadata' : 'none'"
        ></video>
        <div v-else-if="slide.type === 'video'" class="w-full h-full bg-slate-900"></div>

        <img
          v-else
          :src="shouldLoadSlide(index) ? slide.url : TRANSPARENT_PIXEL"
          :alt="slide.alt || slide.name"
          class="w-full h-full hero-media transition-transform duration-1000"
          :loading="currentSlide === index ? 'eager' : 'lazy'"
          :fetchpriority="currentSlide === index ? 'high' : 'low'"
          decoding="async"
          @error="handleImageError($event, slide)"
        />
      </div>
      <!-- Lighter Overlay - More visible images -->
        <!-- Lighter Overlay - More visible images + subtle vignette -->
        <div class="absolute inset-0 bg-gradient-to-t from-black/30 via-black/10 to-black/0 pointer-events-none"></div>
        <div class="absolute inset-0 pointer-events-none" style="background: radial-gradient(ellipse at center, rgba(0,0,0,0) 40%, rgba(0,0,0,0.25) 100%);"></div>
    </div>

    <!-- Content -->
      <div
        class="relative z-10 h-full flex flex-col justify-between px-4 sm:px-6 lg:px-8 py-8"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
        @focusin="onFocusIn"
        @focusout="onFocusOut"
      >

      <!-- Top: Company Badge -->
      <div class="text-center pt-4">
        <span class="inline-block text-white/50 text-xs tracking-widest uppercase drop-shadow-sm">
          Est. 2005 • Naples, Italy
        </span>
      </div>

      <!-- Center: Main Title -->
      <div class="text-center">
        <p class="text-white/60 text-sm sm:text-base mb-2 tracking-wide hero-badge">
            {{ $t('home.hero.badge') }}
          </p>
          <div class="inline-block bg-black/30 backdrop-blur-sm px-4 py-2 rounded-md">
            <h1 class="text-2xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-white/95 leading-tight max-w-3xl mx-auto drop-shadow-lg">
              {{ $t('home.hero.title') }}
            </h1>
          </div>
      </div>

      <!-- Bottom: Action Buttons + Navigation -->
      <div class="space-y-6">
        <!-- Action Buttons - Simple & Clean -->
        <div class="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
          <NuxtLink
            to="/machines"
            class="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-slate-900 font-semibold rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Search machines"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {{ $t('home.actions.searchMachine.title') }}
          </NuxtLink>
          <NuxtLink
            to="/contact?inquiry=sell#contact-form"
            class="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-slate-900 transition-colors"
            aria-label="Sell your machine - contact"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {{ $t('home.actions.sellMachine.title') }}
          </NuxtLink>
        </div>

        <!-- Slide Indicators -->
        <div class="flex items-center justify-center gap-2">
          <button
            v-for="(slide, index) in slides"
            :key="index"
            @click="goToSlide(index)"
            class="w-2 h-2 rounded-full transition-all duration-300"
            :class="currentSlide === index ? 'bg-white w-6' : 'bg-white/40 hover:bg-white/60'"
            :aria-label="`Go to slide ${index + 1}`"
          />
        </div>
      </div>
    </div>

    <!-- Navigation Arrows -->
    <button
      @click="prevSlide"
      class="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-black/30 hover:bg-black/50 rounded-full flex items-center justify-center text-white transition-colors"
      aria-label="Previous slide"
    >
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
      </svg>
    </button>
    <button
      @click="nextSlide"
      class="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-black/30 hover:bg-black/50 rounded-full flex items-center justify-center text-white transition-colors"
      aria-label="Next slide"
    >
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
      </svg>
    </button>
  </section>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';

// Current slide index
const currentSlide = ref(0);
let intervalId = null;
const AUTOPLAY_INTERVAL_MS = 6000;
const PRELOAD_RADIUS = 1;
const TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';
const isReducedMotion = ref(false);
const isPageVisible = ref(true);

// Use the central image URL resolver
const { getSafeImageUrl } = useImageHandling();

// ── Fallback slides shown when the DB is empty or the API fails ──────────────
const FALLBACK_SLIDES = [
  {
    id: 'f1',
    name: 'Heidelberg Speedmaster CX 104',
    type: 'image',
    url: '/images/57322_Motiv_2_1c_150dpi_STAGE_BIG.jpg',
    fallbackImage: '/images/HEIDELBERG-Cartonmaster-CX-145_closeup_STAGE_BIG.jpg',
    description: 'High-performance sheetfed offset press',
  },
  {
    id: 'f2',
    name: 'Machine 1',
    type: 'image',
    url: '/images/machine1.jpg',
    description: 'Featured machine 1',
  },
  {
    id: 'f3',
    name: 'Banner',
    type: 'image',
    url: '/images/Gemini_Generated_Image_nxfox1nxfox1nxfo.png',
    description: 'Featured machine 2',
  },
  {
    id: 'f4',
    name: 'Large Sheetfed Press',
    type: 'image',
    url: '/images/speedmaster-sx102-full-stage-1920x1080_STAGE_BIG.jpg',
    description: 'Robust sheetfed press — side view',
  },
  {
    id: 'f5',
    name: 'Printing Workshop',
    type: 'video',
    url: '/images/HEIDELBERG_Speedmaster_CX_104_loop-homepage.mp4',
    description: 'State-of-the-art printing workshop',
  },
];

// Reactive slides list – filled from API, fallback shown until loaded
const slides = ref([...FALLBACK_SLIDES]);

// ── Fetch dynamic slides from DB via public API ───────────────────────────────
const fetchSlides = async () => {
  try {
    const config = useRuntimeConfig();
    const apiBase = config.public.apiBase || '/api/v1';
    const res = await $fetch(`${apiBase}/carousel`).catch(() => null);

    if (res?.data && Array.isArray(res.data) && res.data.length > 0) {
      slides.value = res.data.map((item) => ({
        id:          item.id,
        name:        item.file_name || 'Slide',
        type:        item.media_type === 'video' ? 'video' : 'image',
        // Prefer the explicit storage_url the controller now provides,
        // then fall back to the DB file_path resolved by useImageHandling
        url:         item.storage_url
                       ? getSafeImageUrl(item.storage_url)
                       : getSafeImageUrl(item.file_path),
        alt:         item.alt_text?.fr || item.alt_text?.en || item.file_name || 'Carousel slide',
        description: '',
      }));
      currentSlide.value = 0;
    }
    // If API returns empty array, fallback slides stay in place
  } catch {
    // Network error – fallback slides remain
  }
};


const slideCount = computed(() => slides.value.length);

const normalizeIndex = (index) => {
  if (slideCount.value === 0) return 0;
  return (index + slideCount.value) % slideCount.value;
};

const shouldLoadSlide = (index) => {
  if (slideCount.value <= 2) return true;

  const activeIndex = currentSlide.value;
  const forwardDistance = (index - activeIndex + slideCount.value) % slideCount.value;
  const backwardDistance = (activeIndex - index + slideCount.value) % slideCount.value;

  return Math.min(forwardDistance, backwardDistance) <= PRELOAD_RADIUS;
};

const nextSlide = () => {
  currentSlide.value = normalizeIndex(currentSlide.value + 1);
  resetAutoPlay();
};

const prevSlide = () => {
  currentSlide.value = normalizeIndex(currentSlide.value - 1);
  resetAutoPlay();
};

const goToSlide = (index) => {
  currentSlide.value = normalizeIndex(index);
  resetAutoPlay();
};

const resetAutoPlay = () => {
  if (slideCount.value <= 1) return;
  stopAutoPlay();
  startAutoPlay();
};

const handleImageError = (event, slide) => {
  const target = event.target;

  if (target.dataset.fallbackApplied === '1') {
    return;
  }

  target.dataset.fallbackApplied = '1';
  target.src = slide.fallbackImage || 'https://placehold.co/1920x1080/1e293b/ffffff?text=Technomac+SNC';
};

// Autoplay (pauses on hover/focus)
const startAutoPlay = () => {
  if (slideCount.value <= 1 || isReducedMotion.value || !isPageVisible.value) return;
  stopAutoPlay();
  intervalId = setInterval(() => {
    currentSlide.value = normalizeIndex(currentSlide.value + 1);
  }, AUTOPLAY_INTERVAL_MS);
};

const stopAutoPlay = () => {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
};

// Pause on hover/focus and enable keyboard navigation
const onMouseEnter = () => stopAutoPlay();
const onMouseLeave = () => startAutoPlay();
const onFocusIn = () => stopAutoPlay();
const onFocusOut = () => startAutoPlay();

const onVisibilityChange = () => {
  isPageVisible.value = document.visibilityState === 'visible';

  if (isPageVisible.value) {
    startAutoPlay();
    return;
  }

  stopAutoPlay();
};

const onKeydown = (e) => {
  if (e.key === 'ArrowRight') nextSlide();
  if (e.key === 'ArrowLeft') prevSlide();
};

onMounted(async () => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    isReducedMotion.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Fetch DB slides; fallback slides show instantly while loading
  await fetchSlides();

  startAutoPlay();
  window.addEventListener('keydown', onKeydown);
  document.addEventListener('visibilitychange', onVisibilityChange);
});

onUnmounted(() => {
  stopAutoPlay();
  window.removeEventListener('keydown', onKeydown);
  document.removeEventListener('visibilitychange', onVisibilityChange);
});
</script>

<style scoped>
/* Hero media: fill carousel and apply zoom-out when active */
.hero-media {
  object-fit: cover;
  transform-origin: center center;
  transform: scale(1);
}
.transition-transform.duration-1000 {
  transition: transform 1000ms ease;
}
.absolute.inset-0 > .hero-media {
  /* default small padding to avoid edge clipping */
  padding: 0;
}
/* When slide is visible (opacity-100 z-10 applied), subtle zoom-in for effect */
.opacity-100 .hero-media,
.z-10 .hero-media {
  transform: scale(1.02);
}

/* Make indicators and text a bit crisper */
.inline-block.bg-black\/30 { background-color: rgba(0,0,0,0.32); }

/* Text-only shadow for badge paragraph - affects letters, not element box */
.hero-badge {
  text-shadow: 0 2px 6px rgba(0,0,0,0.6);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

@media (prefers-reduced-motion: reduce) {
  .hero-media,
  .transition-transform.duration-1000 {
    transition: none !important;
    transform: none !important;
  }
}
</style>
